import { useAppDispatch } from "@/store/hooks";
import { updateSlide } from "@/store/slideEditorSlice";
import type { SlideFields } from "@/types/Slide";
import type { WordVocabularySlideData } from "@ai-teach/types";
import type { MediaInput, SlideHandlers } from "../types";

/**
 * Handlers for word slide type
 * Word slides have a fixed layout with specific fields (word, image, audio, syllables)
 * They don't use the canvas element system like other slide types
 */
export const useWordVocabularyHandlers = (slide: SlideFields | null): SlideHandlers => {
  const dispatch = useAppDispatch();

  const vocabSlideData = slide?.slideData as WordVocabularySlideData | undefined;

  if (!slide || !vocabSlideData) {
    // Return no-op functions if no slide data
    return {
      addImage: () => {},
      addAudio: () => {},
      addVideo: () => {},
    };
  }

  const slideId = slide.id;

  // The card shows either an image or a video, so adding one switches the card to it.
  const addImage = ({ src, id, filename }: MediaInput) => {
    dispatch(
      updateSlide({
        slideId,
        slideData: { ...vocabSlideData, image: { src, id, filename }, mediaType: "image" },
      })
    );
  };

  const addVideo = ({ src, id, filename }: MediaInput) => {
    dispatch(
      updateSlide({
        slideId,
        slideData: { ...vocabSlideData, video: { src, id, filename }, mediaType: "video" },
      })
    );
  };

  const addAudio = ({ src, id, filename }: MediaInput) => {
    dispatch(
      updateSlide({ slideId, slideData: { ...vocabSlideData, audio: { src, id, filename } } })
    );
  };

  return {
    addImage,
    addVideo,
    addAudio,
  };
};
